// @flow
import { useEffect, useState } from 'react';

// Components
import Slider from 'rc-slider';
import { Link } from 'react-router-dom';
import AddBlendToCartButton from '../../blend/containers/AddBlendToCartButton';
import {
  ItemListImage,
  OptionSwitchCard,
  quantityConverter,
} from '../../common';
import InputFieldErrorMessage from '../../common/components/InputFieldErrorMessage';
import PriceInformationNotice from '../../common/components/PriceInformationNotice';
import LoadingBar from './LoadingBar';

// Actions
import { updateBlendQuantity } from '../../blend/actions';

// Selectors
import { editingItemLoadingByIdSelector } from '../selectors';

// Composers
import { connect } from 'react-redux';

// Styles
import 'rc-slider/assets/index.css';
import 'rc-tooltip/assets/bootstrap.css';
import './Item.css';

// Assets
import EmptyBlend from '../../blend/images/blend_box.png';
import QuestionMarkIcon from '../images/question-mark.svg';
import PortioningValuesBox from './PortioningValuesBox';

function BlendItem(props) {
  const { item, variant, updatingItemLoading, handleChangePortion } = props;

  const [observation, setObservation] = useState(item.observation || '');
  const [badObservation, setBadObservation] = useState('');
  const [hasObservation] = useState(!!item.observation);
  const [isPortioned, setIsPortioned] = useState(!!item.portioningSize);
  const [portioningSize, setPortioningSize] = useState(
    item.portioningSize || null,
  );
  const [sliderMaxQuantity, setSliderMaxQuantity] = useState(item.quantity);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const debounce = setTimeout(() => {
      handleChangePortion(portioningSize);
    }, 500);
    return () => clearTimeout(debounce);
  }, [portioningSize, observation]);

  useEffect(() => {
    if (item.portioningSize !== portioningSize) {
      setIsPortioned(!!item.portioningSize);
      setPortioningSize(item.portioningSize);
    }
  }, [item.portioningSize]);

  const handleDelete = () => {
    props.handleRemoveBlend(item.id);
  };

  const handleChangeObservation = e => {
    const val = e.target.value;
    if (val.length < 100) {
      setObservation(val);
      setBadObservation(val.length === 99 ? 'Observação muito longa' : '');
    }
  };

  const renderDeleteItemButton = className => (
    <div className={className}>
      <button
        className="item-list-trash-button"
        aria-hidden="true"
        onClick={handleDelete}
        disabled={updatingItemLoading}
      >
        Excluir
      </button>
    </div>
  );

  const generateMarks = (min, max) => {
    const marks = {};
    const start = min >= 1000 ? min : 1000;
    for (let i = start; i <= max; i += 1000) {
      marks[i] = `${i / 1000}kg`;
    }
    return marks;
  };

  const minQuantity = Math.max(Math.ceil(item.quantity / 30 / 100) * 100, 500);
  const maxQuantity = Math.min(10000, sliderMaxQuantity);
  const incrementStep = 250;

  return (
    <div className="item-list-main-container">
      <div className="item-list-image-container">
        <section className="search-result-square-image-section-cart">
          {variant ? (
            <ItemListImage isBlend={true} variant={variant} />
          ) : (
            <Link to="/blend/meus-blends/">
              <img
                className="blend-hit-box-image"
                src={EmptyBlend}
                alt="Nenhum item no blend"
              />
            </Link>
          )}
        </section>
        {windowWidth < 768 && (
          <div className="item-list-header">
            <div className="line-item-content">
              <p>Blend</p>
              <Link to="/blend/meus-blends/">
                <h5 className="line-item-name-text">{item.name}</h5>
              </Link>
            </div>
          </div>
        )}
      </div>

      <div className="item-list-content">
        {windowWidth >= 768 && (
          <div className="item-list-header">
            <div className="line-item-content">
              <p>Blend</p>
              <Link to="/blend/meus-blends/">
                <h5 className="line-item-name-text">{item.name}</h5>
              </Link>
            </div>
          </div>
        )}

        <div className="item-list-container">
          <div className="item-informations-edit-container">
            <div className="item-portioning-edit-container">
              <OptionSwitchCard
                title="Porcionar Produto"
                description="Tenha seu produto separado do seu jeito."
                badgeText="Opcional"
                value={isPortioned}
                onChange={value => {
                  setIsPortioned(value);
                  setPortioningSize(item.quantity);
                }}
              />

              {isPortioned && (
                <>
                  <div className="item-portioning-edit">
                    <div className="item-slider-content">
                      <Slider
                        min={minQuantity}
                        max={maxQuantity}
                        step={incrementStep}
                        marks={generateMarks(minQuantity, maxQuantity)}
                        defaultValue={
                          portioningSize >= minQuantity
                            ? portioningSize
                            : minQuantity
                        }
                        value={portioningSize || minQuantity}
                        onChange={value => setPortioningSize(value)}
                        disabled={!isPortioned}
                        trackStyle={{ backgroundColor: '#eb5b1e' }}
                        handleStyle={{ borderColor: '#ce3c25' }}
                      />
                    </div>
                    <div className="item-portioning-size-block">
                      <span>
                        {quantityConverter(
                          portioningSize,
                          variant?.unitForQuantity || 'g',
                        )}
                      </span>
                    </div>
                  </div>

                  {portioningSize && (
                    <PortioningValuesBox
                      quantity={item.quantity}
                      portioningSize={portioningSize}
                    />
                  )}
                </>
              )}
            </div>

            <div className="line-item-observation-container">
              {hasObservation && (
                <>
                  <OptionSwitchCard
                    title="Adicionar Observação"
                    description="Especifique algum detalhe do seu pedido."
                    badgeText="Opcional"
                    value={hasObservation}
                    onChange={() => {}}
                  />

                  <div className="line-item-observation-main">
                    <form>
                      <InputFieldErrorMessage message={badObservation} />
                      <textarea
                        placeholder="Observações para a produção"
                        value={observation}
                        disabled={true}
                        onChange={e => handleChangeObservation(e)}
                      />
                    </form>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="item-list-price-container">
          <div className="line-item-price-content">
            {renderDeleteItemButton('line-item-price-container-option')}
            <AddBlendToCartButton
              blendTemplateId={item.templateId}
              unitForQuantity={'g'}
              incrementQuantity={item.incrementQuantity}
              blend={item}
              clickable={true}
              portionQuantity={portioningSize}
              setPortionQuantity={setPortioningSize}
              setSliderMaxQuantity={setSliderMaxQuantity}
            />
          </div>
          <div className="line-item-estimated-price">
            <p className="line-item-estimated-price-title">Preço Estimado</p>
            <div className="line-item-understand-the-price">
              <img src={QuestionMarkIcon} alt="entenda o preco" />
              <span>Entenda o preço</span>
              <div className="line-item-price-information-notice">
                <PriceInformationNotice />
              </div>
            </div>
            <div className="line-item-estimated-price-values">
              <h6 className="line-item-product-total-price">
                {(item.price * item.quantity).toLocaleString('pt-br', {
                  style: 'currency',
                  currency: 'BRL',
                })}
              </h6>
              <h6 className="line-item-product-price">
                {(item.price * 1000).toLocaleString('pt-br', {
                  style: 'currency',
                  currency: 'BRL',
                })}
                <small>/Kg</small>
              </h6>
            </div>
          </div>
        </div>
      </div>

      <LoadingBar isVisible={props.loading} />
    </div>
  );
}

const mapStateToProps = (state, ownProps) => ({
  updatingItemLoading: editingItemLoadingByIdSelector(
    state,
    ownProps.productId,
  ),
});

const mapDispatchToProps = (dispatch, ownProps) => ({
  handleChangePortion: portioningSize =>
    dispatch(updateBlendQuantity(ownProps.item.id, 0, portioningSize)),
});

export default connect(mapStateToProps, mapDispatchToProps)(BlendItem);
